import { type FC, Fragment, type ReactNode } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import cx from 'classnames';

import useAbsolutePanelViewport from '~/hooks/useAbsolutePanelViewport';

type BottomSheetDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  panelClassName?: string;
  backdropClassName?: string;
  afterLeave?: () => void;
  absolutePanel?: boolean;
  // Skip the enter transition on first mount so the sheet is visible on first paint.
  disableInitialTransition?: boolean;
  zIndexClassName?: string;
};

const absolutePanelTransition = {
  enterFrom: 'opacity-0',
  enterTo: 'opacity-100',
  leaveFrom: 'opacity-100',
  leaveTo: 'opacity-0',
} as const;

const fixedPanelTransition = {
  enterFrom: 'translate-y-full',
  enterTo: 'transform-none',
  leaveFrom: 'transform-none',
  leaveTo: 'translate-y-full',
} as const;

export const BottomSheetDialog: FC<BottomSheetDialogProps> = ({
  isOpen,
  onClose,
  children,
  panelClassName,
  backdropClassName = 'bg-black/60',
  afterLeave,
  absolutePanel = false,
  disableInitialTransition = false,
  zIndexClassName = 'z-50',
}) => {
  const absolutePanelStyle = useAbsolutePanelViewport(absolutePanel && isOpen);
  const panelTransition = absolutePanel ? absolutePanelTransition : fixedPanelTransition;

  return (
    <Transition appear={!disableInitialTransition} show={isOpen} as={Fragment} afterLeave={afterLeave}>
      <Dialog
        as="div"
        onClose={onClose}
        className={cx(zIndexClassName, absolutePanel ? 'absolute inset-x-0' : 'relative')}
        style={absolutePanel ? absolutePanelStyle : undefined}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={cx('fixed inset-0', backdropClassName)} />
        </Transition.Child>

        <div
          className={cx(
            zIndexClassName,
            absolutePanel ? 'absolute inset-0 flex items-end' : 'fixed inset-x-0 bottom-0',
          )}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            leave="ease-in duration-150"
            {...panelTransition}
          >
            <Dialog.Panel
              className={cx('w-full rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)]', panelClassName)}
            >
              {children}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
